import { guestQuoteLinkParamsSchema } from '@repo/api-contracts';
import { z } from 'zod';

/** 연결 대기 보관 자리(G11) — 링크 화면 [주문하기] 때 남기고, ACTIVE 가 되면 앱 셸 훅이 읽어 연결한다. */
const STORAGE_KEY = 'superstructure.pending-guest-quote';

/** 7일 지난 기억은 버린다(G11). */
const PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const pendingGuestQuoteSchema = guestQuoteLinkParamsSchema.extend({ savedAt: z.iso.datetime() });

export interface PendingGuestQuote {
  readonly quoteRequestId: string;
  readonly signature: string;
  /** 남긴 시각(ISO). 7일이 지나면 load 가 버린다. */
  readonly savedAt: string;
}

const clear = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 무시 — 다음 load 가 다시 거른다.
  }
};

/**
 * 비회원 견적 연결 대기 저장소. 가입·이메일 인증이 다른 탭·기기에서 끝나도 이 브라우저가 ACTIVE 가 되는 순간 이어지도록
 * localStorage 에 둔다. 깨졌거나 7일 지난 값은 읽을 때 지운다. storage 오류는 밖으로 내지 않는다.
 */
export const pendingGuestQuoteStorage = {
  save: (
    link: { readonly quoteRequestId: string; readonly signature: string },
    now: Date = new Date(),
  ): void => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          quoteRequestId: link.quoteRequestId,
          signature: link.signature,
          savedAt: now.toISOString(),
        }),
      );
    } catch {
      // 보관 실패는 링크 화면 흐름을 막지 않는다 — 링크를 다시 열어 [주문하기]로 다시 시도할 수 있다.
    }
  },
  load: (now: Date = new Date()): PendingGuestQuote | null => {
    let stored: unknown;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === null) return null;
      stored = JSON.parse(raw);
    } catch {
      clear();
      return null;
    }
    const parsed = pendingGuestQuoteSchema.safeParse(stored);
    if (!parsed.success || now.getTime() - Date.parse(parsed.data.savedAt) > PENDING_TTL_MS) {
      clear();
      return null;
    }
    return parsed.data;
  },
  clear,
} as const;
